import React from "react";
import {
  Phone,
  Instagram,
  Linkedin,
  Send,
  Shield,
  FileText,
  Copyright,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = (i18n.language === 'en');
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="main-footer"
      className="
        relative mt-6
        rounded-2xl overflow-hidden
        bg-gradient-to-br from-slate-100 via-teal-50 to-teal-100/70
        dark:bg-gradient-to-br dark:from-slate-900 dark:via-[#0B2E2F] dark:to-slate-900
        backdrop-blur-xl
        border border-teal-500/15 dark:border-teal-400/15
        shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
        dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
        transition-colors duration-500
      "
    >
      {/* خط تزئینی طلایی بالا */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

      {/* گلوی طلایی گوشه بالا-راست */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:bg-amber-400/10" />

      {/* گلوی teal گوشه پایین-چپ */}
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 blur-3xl dark:bg-teal-500/10" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-5">

        {/* ================= ردیف اصلی ================= */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-5">

          {/* ---------- بخش برند + قوانین ---------- */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">

            <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <Copyright className="w-3.5 h-3.5 text-amber-400/80" />
              <span>
                {currentYear} {t("footer.allRightsFor")}
              </span>
            </span>

            <a
              href="https://rayanpouyesh.ir/"
              target="_blank"
              rel="noopener noreferrer"
              className="
                group/brand relative
                text-[12px] font-bold
                bg-gradient-to-l from-amber-300 via-amber-400 to-amber-500
                bg-clip-text text-transparent
                hover:from-amber-200 hover:via-amber-300 hover:to-amber-400
                transition-all duration-300
                drop-shadow-[0_1px_4px_rgba(251,191,36,0.3)]
              "
            >
              {t("footer.companyName")}
              <span className="absolute -bottom-0.5 inset-x-0 h-[1px] bg-gradient-to-l from-transparent via-amber-300/70 to-transparent opacity-0 group-hover/brand:opacity-100 transition-opacity duration-300" />
            </a>

            <span className="text-[11px] text-teal-500/40 dark:text-teal-400/30">|</span>

            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="
                group/link flex items-center gap-1.5
                text-[11px] font-medium
                text-slate-500 dark:text-slate-400
                hover:text-amber-600 dark:hover:text-amber-200
                transition-colors duration-300
              "
            >
              <FileText className="w-3.5 h-3.5 text-teal-600/70 dark:text-teal-300/70 group-hover/link:text-amber-400 transition-colors duration-300" />
              {t("footer.terms")}
            </a>

            <span className="text-[11px] text-teal-500/40 dark:text-teal-400/30">|</span>

            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="
                group/link flex items-center gap-1.5
                text-[11px] font-medium
                text-slate-500 dark:text-slate-400
                hover:text-amber-600 dark:hover:text-amber-200
                transition-colors duration-300
              "
            >
              <Shield className="w-3.5 h-3.5 text-teal-600/70 dark:text-teal-300/70 group-hover/link:text-amber-400 transition-colors duration-300" />
              {t("footer.privacy")}
            </a>
          </div>

          {/* ---------- بخش تماس + شبکه‌های اجتماعی ---------- */}
          <div className="flex flex-wrap items-center justify-center gap-3">

            {/* تلفن */}
            <a
              href="tel:09157797315"
              className="
                group/phone flex items-center gap-1.5
                px-2.5 py-1.5 rounded-lg
                bg-white/50 dark:bg-white/5
                border border-teal-500/20 dark:border-teal-400/20
                backdrop-blur-md
                transition-all duration-300
                hover:border-amber-300/60 hover:bg-amber-300/10
                hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.5)]
                hover:scale-[1.03]
              "
            >
              <Phone className="w-3.5 h-3.5 text-teal-600 dark:text-teal-300 group-hover/phone:text-amber-500 transition-colors duration-300" />
              <span
                dir="ltr"
                className={`text-[11px] font-bold tabular-nums text-slate-700 dark:text-slate-200 ${isEnglish && 'font-sans'} `}
              >
                0915-779-7315
              </span>
            </a>

            <span className="text-[11px] text-teal-500/40 dark:text-teal-400/30">|</span>

            {/* شبکه‌های اجتماعی */}
            <div className="flex items-center gap-1.5">
              {[
                { icon: Instagram, labelKey: "instagram", href: "#" },
                { icon: Linkedin, labelKey: "linkedin", href: "#" },
                { icon: Send, labelKey: "telegram", href: "#" },
              ].map(({ icon: Icon, labelKey, href }) => (
                <a
                  key={labelKey}
                  href={href}
                  aria-label={t(`footer.social.${labelKey}`)}
                  className="
                    group/social relative
                    flex items-center justify-center
                    h-8 w-8 rounded-lg
                    text-slate-400 dark:text-slate-500
                    bg-white/40 dark:bg-white/5
                    border border-teal-500/15 dark:border-teal-400/15
                    backdrop-blur-md
                    transition-all duration-300
                    hover:scale-110 hover:-translate-y-0.5
                    hover:text-amber-500 dark:hover:text-amber-200
                    hover:border-amber-300/60
                    hover:bg-amber-300/10
                    hover:shadow-[0_6px_20px_-6px_rgba(251,191,36,0.6)]
                  "
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* ================= جداکننده گرادیانتی ================= */}
        <div className="my-4 h-[1px] bg-gradient-to-l from-transparent via-teal-400/25 to-transparent" />

        {/* ================= ردیف پایین ================= */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">

          {/* کپی‌رایت طراحی */}
          <div className="flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500">
            <Sparkles className="w-3 h-3 text-amber-400/70" />
            <span>{t("footer.designedBy")}</span>
            <a
              href="https://rayanpouyesh.ir/"
              target="_blank"
              rel="noopener noreferrer"
              className="
                font-bold
                text-teal-700 dark:text-amber-200
                hover:text-amber-500 dark:hover:text-amber-100
                transition-colors duration-300
              "
            >
              {t("footer.companyName")}
            </a>
          </div>

          {/* نقطه‌های تزئینی */}
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400/70 shadow-[0_0_6px_rgba(251,191,36,0.7)]" />
            <span className="h-1.5 w-1.5 rounded-full bg-teal-400/70 shadow-[0_0_6px_rgba(45,212,191,0.7)]" />
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400/70 shadow-[0_0_6px_rgba(251,191,36,0.7)]" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;